import { ActivatedRouteSnapshot, CanActivateFn, CanMatchFn, RouterStateSnapshot } from '@angular/router';
import { AuthGuardData, createAuthGuard } from 'keycloak-angular';
import { inject } from '@angular/core';
import Keycloak from 'keycloak-js';
import {
    BACK_OFFICE_FLAG,
    PLATFORM_ADMIN_ROLE,
    isPlatformRealmActive,
    isPlatformRealmConfigured,
} from '../config/platform-realm';
import { isFeatureEnabled } from '../config/runtime-config';

/**
 * Whether the back office exists in this environment at all.
 *
 * A `canMatch` guard rather than a `canActivate` one: with the flag off the
 * router keeps looking and the URL falls through to the catch-all, so
 * `/platform` behaves exactly as it did before this feature — a path nobody
 * implemented. The flag itself is only known once /config.json has been read,
 * which is why it is asked here, per navigation, and not while the route table
 * is being built.
 *
 * It asks the environment as well as the flag. The flag alone would match the
 * route where no platform realm is configured, and then nothing can activate
 * it — a blank page with nothing in the console, which is worse than the
 * fall-through.
 */
export const canMatchPlatformBackOffice: CanMatchFn = () =>
    isFeatureEnabled(BACK_OFFICE_FLAG) && isPlatformRealmConfigured();

/**
 * Who may administer tenants: a caller authenticated against the platform realm
 * who carries that realm's operator role.
 *
 * Fails closed, and deliberately checks both halves. The realm role alone is not
 * enough — `realm_access.roles` is whatever the realm this page load logged into
 * put there, so without the realm check a business realm that happened to define
 * a role of the same name would open the back office. The backend refuses such a
 * token anyway (its PlatformAdmin policy accepts the platform scheme only); this
 * keeps the UI from inviting a request that can only come back 401.
 */
const isPlatformAdmin = async (
    _route: ActivatedRouteSnapshot,
    _state: RouterStateSnapshot,
    authData: AuthGuardData,
): Promise<boolean> => {
    //#region An anonymous caller meets the login of the realm this page load chose
    if (!authData.authenticated) {
        inject(Keycloak).login();
        return false;
    }
    //#endregion

    //#region Both halves, or nothing
    return isPlatformRealmActive() && authData.grantedRoles.realmRoles.includes(PLATFORM_ADMIN_ROLE);
    //#endregion
};

export const canActivatePlatformAdmin = createAuthGuard<CanActivateFn>(isPlatformAdmin);
